import { groupBy } from "@foretagsplatsen/core-helpers/array";
import ExpiredForecastColumn from "./ExpiredForecastColumn.js";
import NestedMap from "../../../core/NestedMap.js";

/**
 * Maintain a cache of which cell inherits which rule template. A cell
 * inherit the rule templates of the cells on its left. If no cell on
 * the left defines any rule template of its own, the rule template is
 * inherited from the parent row.
 *
 * @param {AbstractForecastReport} forecastReport - The report containing the cells
 * @param {function} getOwnRuleTemplateFor - Takes {row,column} as
 *   argument and should return the rule template stored at this
 *   location if any or `undefined`
 * @param {RuleTemplate[]} expiredRuleTemplates - List of RuleTemplates whose
 *   MonthPeriod is older than the reference periods. It should be sorted, oldest first.
 */
export default class RuleTemplateInheritingCache {
	constructor({
		forecastReport,
		getOwnRuleTemplateFor,
		expiredRuleTemplates = [],
	}) {
		this._forecastReport = forecastReport;
		this._getOwnRuleTemplateFor = getOwnRuleTemplateFor;
		this._expiredRuleTemplates = expiredRuleTemplates;

		// A `ReportDataSet` mapping each cell of `report` to the rule
		// template inherited by this cell:
		this._cacheNestedMap = undefined;
		this._expiredColumns = this._makeExpiredColumns();
	}

	getInheritedRuleTemplateFor({ row, column }) {
		if (!this._cacheNestedMap) {
			this._fillDataSet();
		}

		const data = this._cacheNestedMap.get([row, column]);

		// getData() returns 0 instead of undefined if no data but we
		// really want to return undefined:
		return data || undefined;
	}

	addRuleTemplate(_ruleTemplate) {
		// Request to recompute everything. A better implementation
		// could invalidate only the row the rule template is added to
		// and its child rows.
		this._cacheNestedMap = undefined;
	}

	removeRuleTemplate(_ruleTemplate) {
		// Request to recompute everything. A better implementation
		// could invalidate only the row the rule template is removed
		// from and its child rows.
		this._cacheNestedMap = undefined;
	}

	_fillDataSet() {
		this._cacheNestedMap = new NestedMap();

		this._fillRows(this._forecastReport.getRows());
	}

	_fillRows(rows = []) {
		rows.forEach((row) => this._fillRow(row));
	}

	_fillRow(row) {
		let currentRuleTemplate = undefined;

		for (const column of this._getExpiredAndReportColumns()) {
			const expiredRuleTemplate =
				column instanceof ExpiredForecastColumn &&
				column.getRuleTemplate({ row });

			currentRuleTemplate =
				expiredRuleTemplate ||
				this._getOwnRuleTemplateFor({ row, column }) ||
				currentRuleTemplate;

			const cellRuleTemplate =
				currentRuleTemplate ||
				this._getRuleTemplateFromParent({ row, column });

			if (cellRuleTemplate) {
				this._cacheNestedMap.set([row, column], cellRuleTemplate);
			}
		}

		this._fillRows(row.getRows());
	}

	_getRuleTemplateFromParent({ row, column }) {
		return (
			row.getParent().isRow() &&
			this._cacheNestedMap.get([row.getParent(), column])
		);
	}

	_getExpiredAndReportColumns() {
		return [...this._expiredColumns, ...this._forecastReport.getColumns()];
	}

	_makeExpiredColumns() {
		const groups = groupBy({
			array: this._expiredRuleTemplates,
			getKey: (ruleTemplate) => ruleTemplate.getStart(),
		});

		return groups.map(
			(group) =>
				new ExpiredForecastColumn({
					ruleTemplates: group.value,
					start: group.key,
				}),
		);
	}
}
